import { ChangeDetectionStrategy, Component, input, OnInit, output, signal } from '@angular/core';
import { DsBoxModule, DsButtonModule } from '@bmw-ds/components';

import { ExtendedCustomer } from '../../../customer/model/extended-customer.model';
import { useI18n } from '../../../i18n/services/i18n.service';
import { ButtonRowComponent } from '../../../modal/button/button-row.component';
import { CustomerComparisonField } from '../../model/customer-comparison-field.model';
import { CustomerComparisonRowContainer } from '../customer-comparison-row/customer-comparison-row.container';

@Component({
  selector: 'cdf-customer-comparison',
  imports: [DsBoxModule, CustomerComparisonRowContainer, DsButtonModule, ButtonRowComponent],
  template: `
    <div class="container-fluid" [hidden]="isLoading()">
      <div class="row">
        <span class="text-regular col-auto">{{ i18n('nscSync.customerComparison.description') }}</span>
      </div>

      <!-- Header row, describing which column contains which values -->
      <ds-box class="comparison-area">
        <div class="row header-row">
          <div class="col-sm-4">{{ i18n('nscSync.headerBarFieldName') }}</div>
          <div class="col">{{ i18n('nscSync.headerBarRetailData') }}</div>
          <div class="col">{{ i18n('nscSync.headerBarNscData') }}</div>
          <div class="col">
            <!-- Empty div for formatting purposes -->
          </div>
        </div>

        <div class="scrollable-area">
          <!-- Rows for each differing field between NSC Database and Retail CRM -->
          @for (comparisonField of comparisonFields; track $index) {
            <cdf-customer-comparison-row-container
              [(combinedCustomer)]="combinedCustomer"
              [comparisonField]="comparisonField"
              [retailCustomer]="retailCustomer()"
              [nscCustomer]="nscCustomer()"
            />
          }
        </div>

        <!-- Buttons to select all values from one of the source systems -->
        <div class="row justify-content-end select-all-button-row">
          <div class="col-sm-4">
            <!-- Empty div for formatting purposes -->
          </div>

          <div class="col">
            <ds-button
              id="retail-select-all-button"
              class="retail-select-all-button"
              variant="outline"
              (click)="selectRetailValues()"
            >
              {{ i18n('nscSync.selectAllButton') }}
            </ds-button>
          </div>
          <div class="col">
            <ds-button id="nsc-select-all-button" variant="outline" (click)="selectNscValues()">
              {{ i18n('nscSync.selectAllButton') }}
            </ds-button>
          </div>
          <div class="col">
            <!-- Empty div for formatting purposes -->
          </div>
        </div>
      </ds-box>

      <!-- Navigation buttons -->
      <cdf-button-row class="mt-4x">
        <ds-button id="back-button" variant="outline" [disabled]="isLoading()" (click)="back.emit()">{{
          i18n('button.back')
        }}</ds-button>

        <ds-button id="sync-button" ds-loading (click)="sync.emit(combinedCustomer())" [isLoading]="isLoading()">{{
          i18n('nscSync.customerComparison.button.sync')
        }}</ds-button>
      </cdf-button-row>
    </div>
  `,
  styleUrl: './customer-comparison.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerComparisonComponent implements OnInit {
  protected readonly i18n = useI18n();

  readonly nscCustomer = input.required<ExtendedCustomer>();
  readonly retailCustomer = input.required<ExtendedCustomer>();
  readonly isLoading = input(false);

  readonly back = output();
  readonly sync = output<ExtendedCustomer>();

  protected readonly comparisonFields = Object.values(CustomerComparisonField);
  readonly combinedCustomer = signal<ExtendedCustomer>({});

  ngOnInit(): void {
    // Select values of the NSC Database customer initially
    this.combinedCustomer.set({ ...this.nscCustomer(), accountNumber: this.retailCustomer().accountNumber });
  }

  protected selectRetailValues(): void {
    this.combinedCustomer.set({ ...this.retailCustomer(), gcid: this.nscCustomer().gcid });
  }

  protected selectNscValues(): void {
    this.combinedCustomer.set({ ...this.nscCustomer(), accountNumber: this.retailCustomer().accountNumber });
  }
}
